import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Eye,
  Layers,
  Link2,
  RefreshCw,
  ShieldAlert,
  Siren,
  Ticket,
  MessageSquare,
  Activity,
  Wrench,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import StaffPageHeader from '../../components/StaffPageHeader';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const severityClass = (v) => {
  const key = String(v || '').toLowerCase();
  if (key === 'critical') return 'border-red-500/50 bg-red-500/15 text-red-200';
  if (key === 'high') return 'border-amber-500/50 bg-amber-500/15 text-amber-200';
  if (key === 'medium') return 'border-cyan-500/50 bg-cyan-500/15 text-cyan-200';
  return 'border-slate-500/40 bg-slate-600/10 text-slate-200';
};

const statusClass = (v) => {
  const key = String(v || '').toLowerCase();
  if (['resolved', 'converted', 'sent', 'approved'].includes(key)) return 'border-emerald-500/45 bg-emerald-500/12 text-emerald-200';
  if (['open', 'acknowledged', 'draft', 'queued', 'pending', 'pending_approval'].includes(key)) return 'border-amber-500/45 bg-amber-500/12 text-amber-200';
  if (['rejected', 'failed', 'cancelled'].includes(key)) return 'border-red-500/45 bg-red-500/12 text-red-200';
  return 'border-slate-500/40 bg-slate-600/10 text-slate-200';
};

const typeClass = (v) => {
  const key = String(v || '').toLowerCase();
  if (key.includes('network')) return 'border-cyan-500/45 bg-cyan-500/12 text-cyan-200';
  if (key.includes('billing')) return 'border-amber-500/45 bg-amber-500/12 text-amber-200';
  if (key.includes('churn')) return 'border-fuchsia-500/45 bg-fuchsia-500/12 text-fuchsia-200';
  return 'border-slate-500/40 bg-slate-600/10 text-slate-200';
};

const Badge = ({ children, cls }) => <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{children}</span>;

const fmt = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString('id-ID', { hour12: false });
};

const memberTypeMeta = (sourceType) => {
  const key = String(sourceType || '').toLowerCase();
  if (key === 'ticket') return { tab: 'ticket', label: 'Tiket', icon: Ticket };
  if (key === 'wa_complaint') return { tab: 'whatsapp', label: 'WhatsApp', icon: MessageSquare };
  if (key === 'ai_prediction') return { tab: 'prediction', label: 'Prediksi', icon: Activity };
  return { tab: 'signal', label: 'Sinyal', icon: Wrench };
};

const RootCauseHintCard = ({ cluster }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0b1222] p-4">
    <p className="text-sm font-semibold text-white">Akar Masalah Kemungkinan</p>
    <p className="mt-2 text-sm text-cyan-100">{cluster?.probable_root_cause || 'Belum ada indikasi utama'}</p>
    <div className="mt-3 space-y-2 text-xs text-slate-300">
      <p>Kepercayaan: <span className="text-white">{Math.round((Number(cluster?.confidence || 0)) * 100)}%</span></p>
      <p>
        Sinyal pendukung: {cluster?.source_events_count || 0} event, {cluster?.affected_customers_count || 0} pelanggan terdampak.
      </p>
      <p>
        Ringkasan: cluster ini dibentuk dari gejala serupa dalam jendela waktu yang sama agar operator fokus pada satu insiden utama.
      </p>
    </div>
  </div>
);

const MemberBreakdownGrid = ({ counts }) => (
  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
    {[
      { label: 'Tiket', value: counts.ticket || 0 },
      { label: 'WhatsApp', value: counts.whatsapp || 0 },
      { label: 'Prediksi', value: counts.prediction || 0 },
      { label: 'Sinyal', value: counts.signal || 0 },
    ].map((item) => (
      <div key={item.label} className="rounded-xl border border-white/10 bg-[#0b1222] p-3">
        <p className="text-[11px] uppercase tracking-[0.08em] text-slate-400">{item.label}</p>
        <p className="mt-1 text-2xl font-bold text-white">{item.value}</p>
      </div>
    ))}
  </div>
);

const tabs = [
  { key: 'all', label: 'Semua' },
  { key: 'ticket', label: 'Tiket' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'prediction', label: 'Prediksi' },
  { key: 'signal', label: 'Sinyal Perangkat / Jaringan' },
];

const EmptyMemberState = ({ label }) => (
  <div className="rounded-xl border border-dashed border-white/20 bg-[#0b1222]/70 p-5 text-center">
    <p className="text-sm font-semibold text-white">Belum ada anggota {label}</p>
    <p className="mt-1 text-xs text-slate-400">Anggota yang relevan akan muncul saat sinyal tambahan masuk.</p>
  </div>
);

const ClusterSkeletonLoader = () => (
  <div className="space-y-4">
    <div className="h-20 animate-pulse rounded-2xl bg-slate-700/30" />
    <div className="h-36 animate-pulse rounded-2xl bg-slate-700/30" />
    <div className="h-48 animate-pulse rounded-2xl bg-slate-700/30" />
  </div>
);

export default function AdminIncidentClusterDetail() {
  const { clusterId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cluster, setCluster] = useState(null);
  const [linkedWa, setLinkedWa] = useState([]);
  const [linkedDrafts, setLinkedDrafts] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [showAllMembers, setShowAllMembers] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [clusterRes, waRes, draftRes] = await Promise.all([
        axios.get(`${API}/ai/clusters/${clusterId}`, { headers }),
        axios.get(`${API}/wa/proactive-messages`, { headers, params: { cluster_id: clusterId, limit: 50 } }),
        axios.get(`${API}/ops/preventive-ticket-drafts`, { headers, params: { cluster_id: clusterId, limit: 50 } }),
      ]);
      setCluster(clusterRes.data);
      setLinkedWa(Array.isArray(waRes.data) ? waRes.data : []);
      setLinkedDrafts(Array.isArray(draftRes.data) ? draftRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal memuat detail cluster');
    } finally {
      setLoading(false);
    }
  }, [clusterId, headers]);

  useEffect(() => { load(); }, [load]);

  const members = useMemo(() => Array.isArray(cluster?.members) ? cluster.members : [], [cluster]);

  const breakdown = useMemo(() => {
    const out = { ticket: 0, whatsapp: 0, prediction: 0, signal: 0 };
    members.forEach((m) => {
      const tab = memberTypeMeta(m.source_type).tab;
      out[tab] = (out[tab] || 0) + 1;
    });
    return out;
  }, [members]);

  const filteredMembers = useMemo(() => {
    if (activeTab === 'all') return members;
    return members.filter((m) => memberTypeMeta(m.source_type).tab === activeTab);
  }, [members, activeTab]);

  const visibleMembers = useMemo(() => {
    if (showAllMembers) return filteredMembers;
    return filteredMembers.slice(0, 12);
  }, [filteredMembers, showAllMembers]);

  const recommendedActions = useMemo(() => {
    if (!cluster) return [];
    if (String(cluster.severity).toLowerCase() === 'critical') {
      return ['Buat draf WA area', 'Buat draf tiket preventif', 'Eskalasi teknisi area terdampak'];
    }
    if (String(cluster.severity).toLowerCase() === 'high') {
      return ['Buat draf WA area', 'Siapkan tiket preventif', 'Pantau sinyal ODP/NAS terkait'];
    }
    return ['Pantau cluster', 'Validasi sinyal pendukung', 'Perbarui status bila eskalasi naik'];
  }, [cluster]);

  const timelineEvents = useMemo(() => {
    if (!cluster) return [];
    const events = [
      {
        key: 'first',
        title: 'Cluster pertama terdeteksi',
        time: cluster.first_detected_at,
        detail: `Sinyal awal mulai dikelompokkan sebagai ${cluster.cluster_type}`,
      },
      {
        key: 'last',
        title: 'Cluster diperbarui',
        time: cluster.last_detected_at,
        detail: `Jumlah event sumber: ${cluster.source_events_count}`,
      },
    ];

    linkedWa.forEach((row) => {
      events.push({
        key: `wa-${row.id}`,
        title: 'Draf WA Proaktif dibuat',
        time: row.created_at,
        detail: `${row.message_type} • ${row.approval_status || row.status}`,
      });
    });

    linkedDrafts.forEach((row) => {
      events.push({
        key: `pd-${row.id}`,
        title: 'Draf tiket preventif dibuat',
        time: row.created_at,
        detail: `${row.suggested_issue_type} • ${row.status}`,
      });
    });

    return events.sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0));
  }, [cluster, linkedWa, linkedDrafts]);

  const runClusterAction = useCallback(async (action) => {
    try {
      if (!clusterId) return;
      if (action === 'acknowledge') {
        await axios.post(`${API}/ai/clusters/${clusterId}/acknowledge`, {}, { headers });
      }
      if (action === 'resolve') {
        const ok = window.confirm('Tandai cluster ini sebagai selesai?');
        if (!ok) return;
        await axios.post(`${API}/ai/clusters/${clusterId}/resolve`, {}, { headers });
      }
      if (action === 'create-wa') {
        await axios.post(`${API}/ai/clusters/${clusterId}/create-wa-draft`, {}, { headers });
      }
      if (action === 'create-ticket') {
        await axios.post(`${API}/ai/clusters/${clusterId}/create-ticket-draft`, {}, { headers });
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Aksi cluster gagal');
    }
  }, [clusterId, headers, load]);

  return (
    <div className="min-h-screen bg-[#050a14]">
      {loading ? <StaffPageLoadingOverlay text="Memuat detail cluster..." /> : null}
      <AppShell>
      <PageContainer>
        <StaffPageHeader
          eyebrow="OPERASIONAL INCIDENT"
          title="Klaster Insiden"
          subtitle="Lihat ringkasan, anggota cluster, akar masalah kemungkinan, dan output tindakan terkait."
          actions={(
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={load}><RefreshCw className="mr-2 h-4 w-4" />Muat Ulang</Button>
              <Button variant="outline" className="border-white/20 text-slate-100" onClick={() => runClusterAction('acknowledge')}>Tandai Dipantau</Button>
              <Button variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-100" onClick={() => runClusterAction('resolve')}>Tandai Selesai</Button>
              <Button variant="outline" className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={() => runClusterAction('create-wa')}>Buat Draf WA</Button>
              <Button variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-100" onClick={() => runClusterAction('create-ticket')}>Buat Draf Tiket</Button>
            </div>
          )}
        />

        {error ? (
          <div className="mt-4 rounded-xl border border-red-500/45 bg-red-500/10 p-3 text-red-200 text-sm">{error}</div>
        ) : null}

        {!loading && !cluster ? (
          <div className="mt-4 rounded-xl border border-dashed border-white/20 bg-[#0b1222]/70 p-6 text-center">
            <p className="text-white font-semibold">Cluster tidak ditemukan</p>
          <p className="text-slate-400 text-sm mt-1">Periksa kembali ID cluster atau muat ulang dari AI Control Center.</p>
            <Button className="mt-4" onClick={() => navigate('/dadu/ai-control')}>Kembali ke AI Control Center</Button>
          </div>
        ) : null}

        {!loading && cluster ? (
          <div className="mt-4 grid gap-5 xl:grid-cols-[1fr,330px]">
            <div className="space-y-5">
              <section className="rounded-2xl border border-white/10 bg-[#081121] p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge cls={severityClass(cluster.severity)}>Keparahan {cluster.severity}</Badge>
                  <Badge cls={typeClass(cluster.cluster_type)}>{cluster.cluster_type}</Badge>
                  <Badge cls={statusClass(cluster.status)}>{cluster.status}</Badge>
                  <Badge cls="border-cyan-500/45 bg-cyan-500/12 text-cyan-100">Kepercayaan {Math.round((Number(cluster.confidence || 0)) * 100)}%</Badge>
                </div>
                <h2 className="mt-3 text-xl font-bold text-white">{cluster.title}</h2>
                <p className="mt-1 text-sm text-slate-200">{cluster.summary}</p>
                <div className="mt-3 grid gap-2 text-xs text-slate-300 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border border-white/10 bg-[#0b1222] p-2"><span className="text-slate-400">Pelanggan Terdampak</span><p className="text-white font-semibold">{cluster.affected_customers_count}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0b1222] p-2"><span className="text-slate-400">Event Sumber</span><p className="text-white font-semibold">{cluster.source_events_count}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0b1222] p-2"><span className="text-slate-400">Pertama Terdeteksi</span><p className="text-white font-semibold">{fmt(cluster.first_detected_at)}</p></div>
                  <div className="rounded-lg border border-white/10 bg-[#0b1222] p-2"><span className="text-slate-400">Terakhir Update</span><p className="text-white font-semibold">{fmt(cluster.last_detected_at)}</p></div>
                </div>
                <div className="mt-2 text-xs text-slate-300">
                  Area: <span className="text-white">{cluster.area_ref || '-'}</span> | Site: <span className="text-white">{cluster.site_id || '-'}</span> | ODP: <span className="text-white">{cluster.odp_id || '-'}</span> | NAS: <span className="text-white">{cluster.nas_id || '-'}</span>
                </div>
              </section>

              <section className="rounded-2xl border border-white/10 bg-[#081121] p-4">
                <p className="text-sm font-semibold text-white">Rekomendasi Tindakan</p>
                <ul className="mt-2 space-y-2 text-sm text-slate-200">
                  {recommendedActions.map((a) => (
                    <li key={a} className="flex items-start gap-2 rounded-lg border border-white/10 bg-[#0b1222] p-2"><Siren className="mt-0.5 h-4 w-4 text-cyan-300" />{a}</li>
                  ))}
                </ul>
              </section>

              <section className="rounded-2xl border border-white/10 bg-[#081121] p-4 space-y-3">
                <p className="text-sm font-semibold text-white">Anggota Cluster</p>
                <MemberBreakdownGrid counts={breakdown} />
                <div className="flex flex-wrap gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => { setActiveTab(tab.key); setShowAllMembers(false); }}
                      className={`rounded-full border px-3 py-1 text-xs ${activeTab === tab.key ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-100' : 'border-white/15 bg-[#0b1222] text-slate-300'}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {filteredMembers.length === 0 ? <EmptyMemberState label={tabs.find((t) => t.key === activeTab)?.label || 'cluster'} /> : (
                  <div className="space-y-2">
                    {visibleMembers.map((m) => {
                      const meta = memberTypeMeta(m.source_type);
                      const Icon = meta.icon;
                      return (
                        <div key={m.id} className="rounded-xl border border-white/10 bg-[#0b1222] p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge cls="border-slate-500/40 bg-slate-600/10 text-slate-200">{meta.label}</Badge>
                            <Badge cls="border-cyan-500/45 bg-cyan-500/12 text-cyan-100">{m.signal_type}</Badge>
                            <span className="text-xs text-slate-400">{fmt(m.detected_at)}</span>
                          </div>
                          <p className="mt-1 text-sm text-white">{m.source_type} #{m.source_id}</p>
                          <p className="text-xs text-slate-300">Nilai sinyal: {m.signal_value ?? '-'}</p>
                          <div className="mt-2">
                            <Button size="sm" variant="outline" className="h-7 border-white/20 text-slate-100" onClick={() => navigate('/topology?mode=live')}><Icon className="mr-1 h-3.5 w-3.5" />Lihat Sumber</Button>
                          </div>
                        </div>
                      );
                    })}

                    {filteredMembers.length > visibleMembers.length ? (
                      <div className="pt-1">
                        <Button size="sm" variant="outline" className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={() => setShowAllMembers(true)}>
                          Lihat semua ({filteredMembers.length})
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </section>

              <section className="rounded-2xl border border-white/10 bg-[#081121] p-4">
                <p className="text-sm font-semibold text-white">Aktivitas Cluster</p>
                <div className="mt-3 space-y-2">
                  {timelineEvents.map((ev) => (
                    <div key={ev.key} className="rounded-xl border border-white/10 bg-[#0b1222] p-3">
                      <p className="text-sm font-medium text-white">{ev.title}</p>
                      <p className="text-xs text-slate-300">{ev.detail}</p>
                      <p className="text-[11px] text-slate-400">{fmt(ev.time)}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <aside className="space-y-4">
              <RootCauseHintCard cluster={cluster} />

              <div className="rounded-2xl border border-white/10 bg-[#0b1222] p-4">
                <p className="text-sm font-semibold text-white">Output Terkait</p>
                <div className="mt-3 space-y-2">
                  <div className="rounded-xl border border-white/10 bg-[#081121] p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-300">Draf WA Proaktif</p>
                      <Badge cls={statusClass(linkedWa[0]?.approval_status || linkedWa[0]?.status || 'none')}>{linkedWa[0] ? (linkedWa[0].approval_status || linkedWa[0].status) : 'belum ada'}</Badge>
                    </div>
                    {linkedWa[0] ? (
                      <>
                        <p className="mt-1 text-xs text-slate-400">{fmt(linkedWa[0].created_at)}</p>
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" variant="outline" className="h-7 border-white/20 text-slate-100" onClick={() => navigate(`/dadu/whatsapp-live?proactive_id=${linkedWa[0].id}`)}><Eye className="mr-1 h-3.5 w-3.5" />Lihat</Button>
                        </div>
                      </>
                    ) : <p className="mt-1 text-xs text-slate-400">Belum ada draf WA.</p>}
                  </div>

                  <div className="rounded-xl border border-white/10 bg-[#081121] p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-300">Draf Tiket Preventif</p>
                      <Badge cls={statusClass(linkedDrafts[0]?.status || 'none')}>{linkedDrafts[0]?.status || 'belum ada'}</Badge>
                    </div>
                    {linkedDrafts[0] ? (
                      <>
                        <p className="mt-1 text-xs text-slate-400">{fmt(linkedDrafts[0].created_at)}</p>
                        <div className="mt-2 flex gap-2">
                          <Button size="sm" variant="outline" className="h-7 border-white/20 text-slate-100" onClick={() => navigate(`/dadu/penanganan?preventive_draft_id=${linkedDrafts[0].id}`)}><Link2 className="mr-1 h-3.5 w-3.5" />Buka Sumber</Button>
                        </div>
                      </>
                    ) : <p className="mt-1 text-xs text-slate-400">Belum ada draf tiket preventif.</p>}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#0b1222] p-4">
                <p className="text-sm font-semibold text-white">Panel Aksi</p>
                <div className="mt-3 space-y-2">
                  <Button className="w-full justify-start bg-cyan-500 hover:bg-cyan-400" onClick={() => runClusterAction('create-wa')}><MessageSquare className="mr-2 h-4 w-4" />Buat Draf WA</Button>
                  <Button className="w-full justify-start bg-amber-500 hover:bg-amber-400 text-black" onClick={() => runClusterAction('create-ticket')}><Ticket className="mr-2 h-4 w-4" />Buat Draf Tiket</Button>
                  <Button variant="outline" className="w-full justify-start border-white/20 text-slate-100" onClick={() => runClusterAction('acknowledge')}><ShieldAlert className="mr-2 h-4 w-4" />Tandai Dipantau</Button>
                  <Button variant="outline" className="w-full justify-start border-emerald-500/40 bg-emerald-500/10 text-emerald-100" onClick={() => runClusterAction('resolve')}><CheckCircle2 className="mr-2 h-4 w-4" />Tandai Selesai</Button>
                </div>
              </div>
            </aside>
          </div>
        ) : null}

        {loading ? <div className="mt-4"><ClusterSkeletonLoader /></div> : null}
                  </PageContainer>
    </AppShell>
    </div>
  );
}





